import { ref, watch, computed } from 'vue'
import searchService from '@/services/searchService'

/**
 * Composable for dynamic search with reactive filters
 * Manages search state, filters and product counts
 * OPTIMIZED: Request cache to avoid duplicate calls
 */
export function useDynamicSearch() {
  // Reactive state
  const searchQuery = ref('')
  const selectedFilters = ref([])
  const selectedCategoryId = ref(null)
  const categories = ref([])
  const filters = ref([])
  const totalProducts = ref(0)
  const searchTerms = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const inStockOnly = ref(false)

  // Cache to avoid duplicate calls
  let lastRequestKey = ''
  let pendingRequest = null

  // Debounce timer
  let debounceTimer = null

  /**
   * Generates a unique key for the search parameters
   */
  const getRequestKey = (search, categoryId, filtersList, stockOnly) => {
    return JSON.stringify({
      search: search || '',
      categoryId: categoryId || null,
      filters: (filtersList || []).sort(),
      inStockOnly: !!stockOnly
    })
  }

  /**
   * Executes the dynamic search
   * OPTIMIZED: Avoids duplicate calls using request cache
   */
  const executeSearch = async (categoryIdParam = undefined) => {
    // Clears previous error
    error.value = null

    // Updates category if it came in the parameter
    if (typeof categoryIdParam !== 'undefined') {
      selectedCategoryId.value = categoryIdParam
    }

    // If there is no search nor filters and also no selected category and no stock filter, clear the results
    if (!searchQuery.value.trim() && selectedFilters.value.length === 0 && !selectedCategoryId.value && !inStockOnly.value) {
      categories.value = []
      filters.value = []
      totalProducts.value = 0
      searchTerms.value = []
      lastRequestKey = ''
      return
    }

    // OPTIMIZATION: Checks if there is already an identical pending or recent request
    const requestKey = getRequestKey(searchQuery.value, selectedCategoryId.value, selectedFilters.value, inStockOnly.value)
    
    if (requestKey === lastRequestKey && pendingRequest) {
      console.log('⏭️ Requisição duplicada ignorada - aguardando resposta anterior')
      return pendingRequest
    }

    lastRequestKey = requestKey

    try {
      isLoading.value = true
      console.log('🔍 Executando busca dinâmica:', {
        search: searchQuery.value,
        categoryId: selectedCategoryId.value,
        filters: selectedFilters.value
      })

      // OPTIMIZATION: A single main call
      pendingRequest = searchService.dynamicSearch({
        search: searchQuery.value,
        categoryId: selectedCategoryId.value,
        filters: selectedFilters.value,
        inStockOnly: inStockOnly.value
      })

      const result = await pendingRequest
      pendingRequest = null

      // OPTIMIZATION: Uses the categories from the main call
      // The second call was eliminated - categories always come from the same source
      categories.value = result.categories || []
      filters.value = result.filters || []
      totalProducts.value = result.totalProducts || 0
      searchTerms.value = result.searchTerms || []

      console.log('✅ Busca concluída:', {
        categories: categories.value.length,
        filters: filters.value.length,
        totalProducts: totalProducts.value
      })
    } catch (err) {
      console.error('❌ Erro na busca dinâmica:', err)
      error.value = err.message || 'Erro ao realizar busca'
      categories.value = []
      filters.value = []
      totalProducts.value = 0
      searchTerms.value = []
      pendingRequest = null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Executes search with debounce
   */
  const debouncedSearch = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      executeSearch(selectedCategoryId.value)
    }, 300) // 300ms debounce
  }

  /**
   * Updates the search query
   */
  const updateSearchQuery = (query, categoryIdParam = undefined) => {
    searchQuery.value = query
    if (typeof categoryIdParam !== 'undefined') {
      selectedCategoryId.value = categoryIdParam
    }
    debouncedSearch()
  }

  /**
   * Adds a filter
   */
  const addFilter = (filterId) => {
    if (!selectedFilters.value.includes(filterId)) {
      selectedFilters.value.push(filterId)
      executeSearch(selectedCategoryId.value) // No debounce for filters
    }
  }

  /**
   * Removes a filter
   */
  const removeFilter = (filterId) => {
    const index = selectedFilters.value.indexOf(filterId)
    if (index > -1) {
      selectedFilters.value.splice(index, 1)
      executeSearch(selectedCategoryId.value) // No debounce for filters
    }
  }

  /**
   * Filter toggle (adds if it does not exist, removes if it exists)
   */
  const toggleFilter = (filterId) => {
    if (selectedFilters.value.includes(filterId)) {
      removeFilter(filterId)
    } else {
      addFilter(filterId)
    }
  }

  /**
   * Clears all filters
   */
  const clearFilters = () => {
    selectedFilters.value = []
    executeSearch(selectedCategoryId.value)
  }


  /** Updates the list of selected filters at once */
  const updateSelectedFilters = (list) => {
    selectedFilters.value = Array.isArray(list) ? [...list] : []
    return executeSearch(selectedCategoryId.value)
  }

  /**
   * Clears the search
   */
  const clearSearch = () => {
    searchQuery.value = ''
    executeSearch(selectedCategoryId.value)
  }

  /**
   * Clears everything (search and filters)
   */
  const clearAll = () => {
    searchQuery.value = ''
    selectedFilters.value = []
    categories.value = []
    filters.value = []
    totalProducts.value = 0
    searchTerms.value = []
    selectedCategoryId.value = null
  }

  /**
   * Checks if a filter is selected
   */
  const isFilterSelected = (filterId) => {
    return selectedFilters.value.includes(filterId)
  }

  /**
   * Computed: Categories with products (only > 0)
   */
  const categoriesWithProducts = computed(() => {
    return categories.value.filter(cat => cat.productCount > 0)
  })

  /**
   * Computed: Root filters with products (only > 0)
   */
  const rootFiltersWithProducts = computed(() => {
    return filters.value.filter(filter => {
      // Checks if the root filter or its children have products
      if (filter.productCount > 0) return true
      if (filter.children && filter.children.length > 0) {
        return filter.children.some(child => child.productCount > 0)
      }
      return false
    })
  })

  /**
   * Computed: Indicates whether there is an active search
   */
  const hasActiveSearch = computed(() => {
    return searchQuery.value.trim().length > 0 || selectedFilters.value.length > 0
  })

  /**
   * Computed: Indicates whether there are results
   */
  const hasResults = computed(() => {
    return totalProducts.value > 0
  })
  /** Sets the state of the stock filter */
  const setInStockOnly = (val) => {
    inStockOnly.value = !!val
  }


  // Watch for changes in the selected filters
  watch(selectedFilters, () => {
    console.log('🔄 Filtros alterados:', selectedFilters.value)
  }, { deep: true })

  return {
    // State
    searchQuery,
    selectedFilters,
    categories,
    filters,
    totalProducts,
    searchTerms,
    isLoading,
    error,

    // Computed
    categoriesWithProducts,
    rootFiltersWithProducts,
    hasActiveSearch,
    hasResults,

    // Methods
    updateSearchQuery,
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
    updateSelectedFilters,
    clearSearch,
    clearAll,
    isFilterSelected,
    executeSearch,
    setInStockOnly,

    // New: allows updating category in the dynamic search
    updateCategory: (categoryId) => {
      selectedCategoryId.value = categoryId || null
      // Updates immediately with current state
      executeSearch(selectedCategoryId.value)
    }
  }
}

